import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Drawer, Input, message } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { validateEmail } from '../../utils/email';
import { validatePassword } from '../../utils/password';
import { validatePhone } from '../../utils/phone';
import { sanitizeObject } from '../../utils/object';
import { getClient } from '../../apollo';
import { cloneDeep } from 'lodash';
import {
  ClientEmployeesDocument,
  ClientEmployeesQuery,
  ClientEmployeesQueryVariables,
  useCreateClientEmployeeMutation,
} from '../../graphql/datamodel.gen';

interface CreateEmployeeDrawerProps {
  visible: boolean;
  onClose: () => any;
}

interface FormProps extends FormComponentProps {
  onSubmit: (input: {
    email: string;
    name: string;
    password: string;
    phone?: string;
  }) => Promise<any>;
}
interface FormState {
  creating: boolean;
}

interface FormValues {
  name: string;
  email: string;
  password: string;
  phone?: string;
}

class CreateEmployeeForm extends React.Component<FormProps, FormState> {
  state = {
    creating: false,
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values: FormValues) => {
      if (!err) {
        const sanitizedValues = sanitizeObject<FormValues>(values);

        try {
          message.info({
            content: 'Creando empleado..',
            key: 'create_employee',
          });
          this.setState({
            creating: true,
          });
          await this.props.onSubmit({
            email: sanitizedValues.email,
            password: sanitizedValues.password,
            phone: sanitizedValues.phone,
            name: sanitizedValues.name,
          });

          message.success({
            content: 'Hecho!',
            key: 'create_employee',
          });
        } catch (e) {
          message.error({
            content: 'Error creando empleado',
            key: 'create_employee',
          });
        } finally {
          this.setState({
            creating: false,
          });
          setTimeout(() => {
            message.destroy();
          }, 4000);
        }
      }
    });
  };

  emailValidator = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    const invalid = await validateEmail(value, {
      isRequired: { enabled: true },
      checkAgainstFirebase: {
        enabled: true,
        message: 'Ya existe un empleado con ese email',
      },
    });

    if (invalid) return callback(invalid);
    return callback();
  };

  passwordValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePassword(value, {
      required: true,
    });
    if (invalid) return callback(invalid);
    return callback();
  };

  phoneValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePhone(value, {
      isRequired: {
        enabled: false,
      },
    });
    if (invalid) {
      return callback(invalid);
    }
    return callback();
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    return (
      <Form id={'create-employee-form'} onSubmit={this.handleSubmit}>
        <Form.Item label={'Nombre'} required={true}>
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce un nombre',
              },
              {
                whitespace: true,
                message:
                  'El nombre no puede tener todos los carácteres en blanco',
              },
            ],
          })(<Input placeholder={'Introduce un nombre'} />)}
        </Form.Item>
        <Form.Item label={'Email'} required={true}>
          {getFieldDecorator('email', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce un email',
              },
              {
                validator: this.emailValidator,
              },
            ],
          })(<Input placeholder={'Introduce un email'} />)}
        </Form.Item>
        <Form.Item label={'Contraseña'} required={true}>
          {getFieldDecorator('password', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce una contraseña',
              },
              {
                validator: this.passwordValidator,
              },
            ],
          })(<Input placeholder={'Introduce una contraseña'} />)}
        </Form.Item>
        <Form.Item label={'Teléfono'} required={false}>
          {getFieldDecorator('phone', {
            rules: [
              {
                validator: this.phoneValidator,
              },
              {
                whitespace: true,
                message:
                  'El teléfono no puede tener todos los carácteres en blanco',
              },
            ],
          })(<Input placeholder={'Puedes introducir un teléfono'} />)}
        </Form.Item>
        <Form.Item>
          <Button
            loading={this.state.creating}
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            Crear
          </Button>
        </Form.Item>
      </Form>
    );
  }
}

const MyCreateEmployeeForm = Form.create<FormProps>()(CreateEmployeeForm);

const CreateEmployeeDrawer: React.FC<CreateEmployeeDrawerProps> = (
  props: CreateEmployeeDrawerProps,
) => {
  const [createClientEmployeeMut] = useCreateClientEmployeeMutation({
    client: getClient(),
  });

  const createClientEmployee = async ({
    email,
    password,
    name,
    phone,
  }: {
    email: string;
    password: string;
    name: string;
    phone?: string;
  }) => {
    await createClientEmployeeMut({
      variables: {
        email,
        password,
        name,
        phone,
      },
      update: (proxy, mutationResult) => {
        const client = getClient();

        const employeeList = client.readQuery<
          ClientEmployeesQuery,
          ClientEmployeesQueryVariables
        >({
          query: ClientEmployeesDocument,
        });

        const clone = cloneDeep(employeeList);

        if (
          !clone.clientEmployees.find(
            (employee) =>
              employee.id === mutationResult.data.createClientEmployee.id,
          )
        ) {
          const newEmployeeList = [
            ...clone.clientEmployees,
            mutationResult.data.createClientEmployee,
          ];

          client.writeQuery({
            query: ClientEmployeesDocument,
            data: {
              clientEmployees: newEmployeeList,
            },
          });
          // @ts-ignore
          client.queryManager.broadcastQueries();
        }
      },
    });
    props.onClose();
  };

  return (
    <Drawer
      closable={true}
      destroyOnClose={true}
      width={300}
      maskClosable={true}
      title={'Crear empleado'}
      visible={props.visible}
      onClose={props.onClose}
    >
      {props.visible && (
        <MyCreateEmployeeForm onSubmit={createClientEmployee} />
      )}
    </Drawer>
  );
};

export default CreateEmployeeDrawer;
